<template>
  <div class="booking-pois-container">
    <!-- Compact Loading Overlay -->
    <div v-if="showLoader" class="loading-overlay">
      <div class="loading-spinner-compact">
        <div class="spinner-dot"></div>
        <div class="spinner-dot"></div>
        <div class="spinner-dot"></div>
      </div>
    </div>
    
    <div v-if="showBookingView" class="booking-content">
      <!-- Compact Date Selection Card -->
      <div class="date-selection-section">
        <div class="compact-date-card glass-card" @click="openRangeDatePicker">
          <div class="date-row">
            <div class="date-item">
              <div class="date-info">
                <span class="date-label">{{ $t('booking.start') }}</span>
                <div class="date-value">{{ moment(range.start).format('MMM DD') }}</div>
                <div class="time-value">{{ moment(range.start).format('HH:mm') }}</div>
              </div>
            </div>
            
            <div class="date-arrow">
              <v-icon size="20">mdi-arrow-right</v-icon>
            </div>
            
            <div class="date-item">
              <div class="date-info">
                <span class="date-label">{{ $t('booking.end') }}</span>
                <div class="date-value">{{ moment(range.end).format('MMM DD') }}</div>
                <div class="time-value">{{ moment(range.end).format('HH:mm') }}</div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- Compact Filters and Results Header -->
      <div class="filters-section">
        <div class="compact-results-bar">
          <div class="results-count">
            <v-icon size="18" class="count-icon">mdi-map-marker-multiple</v-icon>
            <span class="count-text">{{ availablePois?.length || 0 }} {{ $t('booking.available') }}</span>
          </div>
          
          <button class="filter-btn-compact" @click="openDialog">
            <v-icon size="18">mdi-filter-variant</v-icon>
            <span>{{ $t('booking.filter') }}</span>
          </button>
        </div>
      </div>

      <!-- POI Grid -->
      <div class="pois-grid" v-if="availablePois?.length > 0">
        <transition-group 
          name="poi-item" 
          tag="div" 
          class="grid-container"
          @enter="onPoiEnter"
          @leave="onPoiLeave"
        >
          <div 
            v-for="(poi, index) in availablePois" 
            :key="poi.id"
            :data-index="index"
            class="poi-card-wrapper"
            @click="goToPoi(poi)"
          >
            <div class="poi-card glass-card">
              <div class="card-gradient-bg"></div>
              
              <div class="poi-header">
                <h3 class="poi-name">{{ poi?.name }}</h3>
                <div class="poi-category">
                  <v-icon size="16">mdi-tag</v-icon>
                  <span>{{ poi?.category?.name }}</span>
                </div>
              </div>

              <div class="poi-details">
                <div class="detail-item">
                  <v-icon size="18" class="detail-icon">mdi-stairs</v-icon>
                  <span>{{ $t('booking.floor') }} {{ poi?.floor?.number }}</span>
                </div>
                
                <div v-if="poi.capacity" class="detail-item">
                  <v-icon size="18" class="detail-icon">mdi-account-group</v-icon>
                  <span>{{ poi.capacity }} {{ $t('booking.people') }}</span>
                </div>
              </div>

              <div class="poi-actions">
                <button 
                  class="book-btn"
                  :disabled="!IS_PIA"
                  @click.stop="goToPoi(poi)"
                >
                  <v-icon size="18">mdi-calendar-plus</v-icon>
                  <span>{{ $t('booking.book') }}</span>
                </button>
              </div>
            </div>
          </div>
        </transition-group>
      </div>

      <!-- Empty State -->
      <div v-else class="empty-state">
        <div class="empty-card glass-card">
          <div class="empty-icon">
            <v-icon size="64">mdi-calendar-remove</v-icon>
          </div>
          <h3 class="empty-title">{{ $t('booking.no-poi') }}</h3>
          <p class="empty-subtitle">{{ $t('booking.custom-date-range') }}</p>
          <button class="glass-btn" @click="openDialog">
            <v-icon size="18">mdi-filter-variant</v-icon>
            <span>{{ $t('booking.filter') }}</span>
          </button>
        </div>
      </div>
    </div>

    <!-- Filter Dialog -->
    <v-dialog
      v-model="dialog"
      max-width="420"
      transition="dialog-bottom-transition"
    >
      <v-card rounded="xl" class="booking-filter-dialog-card">
        <v-card-title
          class="pa-4 d-flex align-center"
          style="background: linear-gradient(135deg, #285096 0%, #23A5E6 100%);"
        >
          <v-icon color="white" class="mr-2">mdi-filter-variant</v-icon>
          <span class="text-h6 font-weight-semibold" style="color: white;">
            {{ $t('booking.filter') }}
          </span>
          <v-spacer />
          <v-btn icon variant="text" color="white" @click="dialog = false">
            <v-icon>mdi-close</v-icon>
          </v-btn>
        </v-card-title>

        <v-divider />

        <v-card-text class="pa-4 booking-filter-dialog-body">
          <div class="filter-item">
            <label class="compact-label">{{ $t('office.building') }}</label>
            <v-select
              :items="buildings"
              item-value="name"
              item-title="name"
              v-model="selectedBuilding"
              class="booking-dialog-select"
              disabled
              return-object
              density="comfortable"
              variant="outlined"
              rounded="lg"
            />
          </div>

          <div class="filter-item">
            <label class="compact-label">{{ $t('booking.floor') }}</label>
            <v-select
              v-model="selectedFloor"
              :items="floors"
              item-title="name"
              item-value="name"
              return-object
              class="booking-dialog-select"
              density="comfortable"
              variant="outlined"
              rounded="lg"
            />
          </div>

          <div class="filter-item">
            <label class="compact-label">{{ $t('booking.category') }}</label>
            <v-select
              v-model="selectedCategory"
              :items="poiCategories"
              item-title="name"
              item-value="name"
              return-object
              class="booking-dialog-select"
              density="comfortable"
              variant="outlined"
              rounded="lg"
            />
          </div>

          <div class="filter-item">
            <label class="compact-label">{{ $t('booking.capacity') }}</label>
            <v-select
              v-model="selectedPersonCount"
              :items="personCount"
              item-title="count"
              item-value="count"
              class="booking-dialog-select"
              density="comfortable"
              variant="outlined"
              rounded="lg"
            />
          </div>
        </v-card-text>

        <v-divider />

        <v-card-actions class="pa-4 booking-dialog-actions">
          <v-btn
            variant="text"
            color="#4E5965"
            rounded="lg"
            class="text-none"
            @click="cancelFilter"
          >
            {{ $t('booking.cancel') }}
          </v-btn>
          <v-spacer />
          <v-btn
            variant="flat"
            color="#285096"
            rounded="lg"
            class="text-none px-6"
            @click="applyFilters"
          >
            <v-icon start size="18">mdi-check</v-icon>
            {{ $t('booking.apply') }}
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Date Picker Dialog -->
    <v-dialog
      v-model="datePickerDialog"
      max-width="600"
      persistent
      transition="dialog-bottom-transition"
    >
      <v-card rounded="xl" class="date-picker-dialog-card">
        <v-card-title
          class="pa-4 d-flex align-center"
          style="background: linear-gradient(135deg, #285096 0%, #23A5E6 100%);"
        >
          <v-icon color="white" class="mr-2">mdi-calendar-clock</v-icon>
          <span class="text-h6 font-weight-semibold" style="color: white;">
            {{ $t('booking.select-date-time') }}
          </span>
          <v-spacer />
          <v-btn icon variant="text" color="white" @click="discardRange">
            <v-icon>mdi-close</v-icon>
          </v-btn>
        </v-card-title>

        <v-divider />

        <v-card-text class="pa-0">
          <v-alert
            v-if="isRangeEqual"
            type="error"
            variant="tonal"
            class="ma-4"
            rounded="lg"
          >
            {{ $t('booking.time-error') }}
          </v-alert>

          <div class="date-picker-wrapper">
            <DatePicker
              v-if="showDatePicker"
              v-model.range="tempRange"
              is-range
              mode="dateTime"
              :rules="timeRules"
              :min-date="new Date()"
              :max-date="moment().add(59, 'days').toDate()"
              is24hr
              :is-dark="false"
              color="#23A5E6"
              @dayclick="(_, event) => { event.target.blur(); }"
              @update:modelValue="validateTimeRange"
            />
          </div>
        </v-card-text>

        <v-divider />

        <v-card-actions class="pa-4 booking-dialog-actions" style="background: #F8FAFC;">
          <v-btn
            variant="text"
            color="#4E5965"
            rounded="lg"
            class="text-none"
            @click="discardRange"
          >
            {{ $t('booking.cancel') }}
          </v-btn>
          <v-spacer />
          <v-btn
            variant="flat"
            color="#285096"
            rounded="lg"
            class="text-none px-6"
            :disabled="isRangeEqual"
            @click="confirmRange"
          >
            <v-icon start size="18">mdi-check</v-icon>
            {{ $t('booking.confirm') }}
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </div>
</template>

<script>
import { defineComponent, ref, computed, onMounted, watch, nextTick } from 'vue';
import { useRoute } from 'vue-router';
import { useI18n } from 'vue-i18n';
import moment from 'moment';
import lodash from 'lodash';
import { gsap } from 'gsap';

import { DatePicker } from 'v-calendar';
import router from '@/router';
import { showToast } from '@/services/utils';
import {
  getReservationsByPoi,
  getPoiCategoryById,
  getFloorById,
  getFloors,
  getPoiCategories,
  getBuildings,
  getPoisByCategoryId,
  isPIA
} from '@/controllers/BaseController';
import { getStoredItem } from '@/services/utils.js';

export default defineComponent({
  name: 'BookingPoisList',
  components: { DatePicker },
  setup() {
    const { t } = useI18n();
    const route = useRoute();
    
    const range = ref({ start: new Date(), end: new Date() });
    const tempRange = ref({ ...range.value });
    const showLoader = ref(true);
    const allPois = ref([]);
    const availablePois = ref([]);
    const poiCategories = ref([]);
    const buildings = ref([]);
    const floors = ref([]);
    const selectedBuilding = ref(null);
    const selectedFloor = ref(null);
    const selectedCategory = ref(null);
    const selectedPersonCount = ref(1);
    const showBookingView = ref(true);
    const datePickerDialog = ref(false);
    const dialog = ref(false);
    const showDatePicker = ref(true);
    const IS_PIA = ref(false);

    const personCount = ref(Array.from({ length: 10 }, (_, i) => ({ count: i + 1 })));
    const originalFilters = ref({ selectedFloor: '', selectedCategory: '', selectedPersonCount: '' });

    const isRangeEqual = computed(() =>
      moment(tempRange.value.start).isSame(tempRange.value.end, 'minute')
    );

    const timeRules = computed(() => [{
      hours: h => h >= 6 && h <= 20,
      minutes: (m, { hours }) => hours === 20 ? m === 0 : m % 15 === 0,
    }]);

    const onPoiEnter = (el) => {
      const index = parseInt(el.dataset.index);
      /* eslint-disable */ 
      gsap.set(el, { opacity: 0, y: 30, scale: 0.9 });
      gsap.to(el, {
        opacity: 1,
        y: 0,
        scale: 1,
        duration: 0.5,
        delay: index * 0.1,
        ease: "back.out(1.7)"
      });
    };

    const onPoiLeave = (el) => {
      gsap.to(el, {
        opacity: 0,
        scale: 0.8,
        duration: 0.3,
        ease: "power2.in"
      });
    };

    const validateTimeRange = async (r) => {
      const start = moment(r.start);
      const end = moment(r.end);
      const adjusted = { ...r };

      if (start.hour() === 20) {
        showToast({ text: t('booking.start-time-error'), type: 'error', duration: 3000 });
        adjusted.start = start.clone().set({ hour: 19, minute: 45 }).toDate();
        adjusted.end = start.clone().add(15, 'minutes').toDate();
      }

      if (end.isSameOrBefore(start)) {
        adjusted.end = start.clone().add(15, 'minutes').toDate();
      }

      if (end.hour() === 20 && end.minute() > 0) {
        adjusted.end = end.clone().startOf('hour').toDate();
      }

      showDatePicker.value = false;
      tempRange.value = adjusted;
      range.value = adjusted;
      await nextTick();
      showDatePicker.value = true;
    };

    const checkInitialTime = () => {
      const now = moment();
      const roundedMinutes = Math.ceil(now.minute() / 15) * 15;
      let start = moment(now).set({ second: 0, millisecond: 0 });

      if (roundedMinutes === 60) {
        start = start.add(1, 'hour').set({ minute: 0 });
      } else {
        start = start.set({ minute: roundedMinutes });
      }

      if (now.hour() > 20 || (now.hour() === 20 && now.minute() > 0)) {
        start.add(1, 'day').set({ hour: 6, minute: 0 });
      }

      if (start.hour() >= 20) {
        start.add(1, 'day').set({ hour: 6, minute: 0 });
      }

      range.value = {
        start: start.toDate(),
        end: start.clone().add(1, 'hour').toDate(),
      };
    };

    const fetchInitialData = async () => {
      try {
        showLoader.value = true;

        const [categories, buildingName, allFloors, allBuildings] = await Promise.all([
          getPoiCategories(),
          getStoredItem('selectedBuilding'),
          getFloors(),
          getBuildings(),
        ]);

        const storedBuilding = lodash.find(allBuildings, { name: buildingName });
        if (!storedBuilding) return;

        selectedBuilding.value = storedBuilding;
        buildings.value = allBuildings;

        const relevantFloors = lodash.chain(allFloors)
          .filter(f => f.building.id === storedBuilding.id && f.active)
          .orderBy('name')
          .value();

        floors.value = [{ id: 1, name: t('booking.all') }, ...relevantFloors];
        selectedFloor.value = floors.value[0];

        const usableCategories = categories.filter(c => !['Showers', 'Restrooms'].includes(c.name));

        const allPoisResult = await Promise.all(
          usableCategories.map(cat => getPoisByCategoryId(cat.id).then(pois => pois.filter(p => p.buildingId === storedBuilding.id)))
        );

        const flat = lodash.flatten(allPoisResult);

        const enrichedCategories = await Promise.all(flat.map(poi => getPoiCategoryById(poi.poiCategories.ids[0])));
        allPois.value = flat;
        poiCategories.value = [{ id: 1, name: t('booking.all') }, ...lodash.uniqBy(lodash.orderBy(enrichedCategories, 'name'), 'id')];
        selectedCategory.value = poiCategories.value.find(c => storedBuilding.name.toLowerCase().includes('hammer') ? c.name?.toLowerCase().includes('meeting') : c.id === 1) || poiCategories.value[0];

        if (IS_PIA.value) {
          selectedCategory.value = poiCategories.value.find(c => c.name?.toLowerCase().includes('meeting')) || poiCategories.value[0];
        }

        checkInitialTime();
        await fetchAvailablePois();
      } catch (e) {
        console.error('Init error:', e);
      } finally {
        showLoader.value = false;
      }
    };

    const fetchAvailablePois = async () => {
      try {
        showLoader.value = true;

        const start = moment(range.value.start).toDate();
        const end = moment(range.value.end).toDate();

        const pois = selectedCategory.value?.id === 1
          ? allPois.value
          : (await getPoisByCategoryId(selectedCategory.value.id)).filter(
              poi => poi.buildingId === selectedBuilding.value?.id
            );

        const available = await Promise.all(
          pois.map(async poi => {
            const reservations = await getReservationsByPoi(poi.id, start, end);
            if (reservations.length === 0) {
              const [category, floor] = await Promise.all([
                getPoiCategoryById(poi.poiCategories.ids[0]),
                getFloorById(poi.floors.ids[0]),
              ]);
              return { ...poi, category, floor };
            }
            return null;
          })
        );

        let filtered = available.filter(Boolean);

        if (selectedFloor.value?.id !== 1) {
          filtered = filtered.filter(poi => poi.floors.ids[0] === selectedFloor.value.id);
        }

        if (selectedPersonCount.value !== 1) {
          filtered = filtered.filter(poi => poi.capacity >= selectedPersonCount.value);
        }

        availablePois.value = filtered;
      } catch (e) {
        console.error('Failed to fetch POIs:', e);
      } finally {
        showLoader.value = false;
      }
    };

    const goToPoi = async poi => {
      let POI = JSON.parse(JSON.stringify(poi))
      try {
        showLoader.value = true
        await router.push({
          name: 'createManageBooking',
          params: {
            building_id: POI.buildingId,
            category_id: POI.category.id,
            poi_id: POI.id,
          },
        })
      } catch (error) {
        console.error('Failed to navigate to POI:', error)
      } finally {
        showLoader.value = false
      }
    };

    const openRangeDatePicker = () => {
      tempRange.value = { ...range.value };
      datePickerDialog.value = true;
    };

    const confirmRange = async () => {
      range.value = { ...tempRange.value };
      datePickerDialog.value = false;
      await fetchAvailablePois();
    };

    const discardRange = () => (datePickerDialog.value = false);

    const openDialog = () => {
      originalFilters.value = {
        selectedFloor: selectedFloor.value,
        selectedCategory: selectedCategory.value,
        selectedPersonCount: selectedPersonCount.value,
      };
      dialog.value = true;
    };

    const cancelFilter = () => {
      dialog.value = false;
      selectedFloor.value = originalFilters.value.selectedFloor;
      selectedCategory.value = originalFilters.value.selectedCategory;
      selectedPersonCount.value = originalFilters.value.selectedPersonCount;
    };

    const applyFilters = async () => {
      dialog.value = false;
      await fetchAvailablePois();
    };

    const handleRouteChange = async () => {
      if (route.name === 'BookingPoisList') {
        showBookingView.value = true;
        await fetchInitialData();
      } else {
        showLoader.value = false;
        showBookingView.value = false;
      }
    };

    onMounted(async () => {
      IS_PIA.value = await isPIA();
      await handleRouteChange();
    });

    watch(route, handleRouteChange);

    return {
      showLoader,
      range,
      tempRange,
      showDatePicker,
      isRangeEqual,
      timeRules,
      buildings,
      floors,
      selectedBuilding,
      selectedFloor,
      selectedCategory,
      selectedPersonCount,
      showBookingView,
      availablePois,
      personCount,
      poiCategories,
      datePickerDialog,
      dialog,
      openDialog,
      cancelFilter,
      applyFilters,
      validateTimeRange,
      openRangeDatePicker,
      confirmRange,
      discardRange,
      goToPoi,
      onPoiEnter,
      onPoiLeave,
      moment,
      IS_PIA,
      t,
    };
  }
});
</script>

<style scoped>
.booking-pois-container {
  min-height: var(--app-viewport-height, 100vh);
  background: linear-gradient(135deg, #F8FAFC 0%, #EDF3FD 100%);
  position: relative;
}

.loading-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(255, 255, 255, 0.9);
  backdrop-filter: blur(8px);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 9999;
}

.loading-spinner-compact {
  display: flex;
  gap: 6px;
}

.spinner-dot {
  width: 10px;
  height: 10px;
  background: #23A5E6;
  border-radius: 50%;
  animation: dot-bounce 1.4s infinite ease-in-out both;
}

.spinner-dot:nth-child(1) { animation-delay: -0.32s; }
.spinner-dot:nth-child(2) { animation-delay: -0.16s; }
.spinner-dot:nth-child(3) { animation-delay: 0s; }

@keyframes dot-bounce {
  0%, 80%, 100% { transform: scale(0.8); opacity: 0.5; }
  40% { transform: scale(1.2); opacity: 1; }
}

.glass-card {
  background: rgba(255, 255, 255, 0.95);
  backdrop-filter: blur(20px);
  border: 1px solid rgba(162, 187, 230, 0.3);
  border-radius: 20px;
  box-shadow: 
    0 4px 20px rgba(40, 80, 150, 0.08),
    inset 0 1px 0 rgba(255, 255, 255, 0.6);
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
}

.glass-card:hover {
  transform: translateY(-2px);
  box-shadow: 
    0 8px 32px rgba(40, 80, 150, 0.12),
    inset 0 1px 0 rgba(255, 255, 255, 0.6);
}

.date-selection-section {
  margin-bottom: 20px;
}

.compact-date-card {
  cursor: pointer;
  overflow: hidden;
  position: relative;
  padding: 18px 22px;
}

.compact-date-card:hover {
  transform: translateY(-2px);
  box-shadow: 0 8px 24px rgba(40, 80, 150, 0.12);
}

.date-row {
  display: flex;
  align-items: center;
  justify-content: space-between;
}

.date-item {
  flex: 1;
  text-align: center;
}

.date-info {
  display: flex;
  flex-direction: column;
  gap: 6px;
}

.date-label {
  font-size: 12px;
  font-weight: 500;
  color: #4E5965;
  text-transform: uppercase;
  letter-spacing: 0.5px;
}

.date-value {
  font-size: 17px;
  font-weight: 600;
  color: #285096;
}

.time-value {
  font-size: 14px;
  font-weight: 500;
  color: #23A5E6;
  background: rgba(35, 165, 230, 0.1);
  padding: 4px 10px;
  border-radius: 8px;
  display: inline-block;
}

.date-arrow {
  color: #23A5E6;
  opacity: 0.7;
  margin: 0 18px;
}

.filters-section {
  margin-bottom: 20px;
}

.compact-results-bar {
  display: flex;
  align-items: center;
  justify-content: space-between;
  flex-wrap: wrap;
  gap: 12px;
  padding: 12px 0;
}

.results-count {
  display: flex;
  align-items: center;
  gap: 10px;
  background: rgba(52, 211, 153, 0.1);
  padding: 10px 16px;
  border-radius: 14px;
  border: 1px solid rgba(52, 211, 153, 0.2);
}

.count-icon {
  color: #34D399;
}

.count-text {
  font-weight: 500;
  color: #34D399;
  font-size: 14px;
}

.filter-btn-compact {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 10px 18px;
  background: rgba(255, 255, 255, 0.9);
  backdrop-filter: blur(10px);
  border: 1px solid rgba(40, 80, 150, 0.3);
  border-radius: 14px;
  color: #285096;
  font-weight: 500;
  font-size: 14px;
  cursor: pointer;
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
}

.filter-btn-compact:hover {
  background: rgba(40, 80, 150, 0.08);
  transform: translateY(-1px);
  box-shadow: 0 4px 12px rgba(40, 80, 150, 0.15);
}

.booking-content {
  padding: 20px;
  max-width: 1400px;
  margin: 0 auto;
}

.pois-grid {
  min-height: 300px;
}

.grid-container {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(320px, 1fr));
  gap: 20px;
}

.poi-card-wrapper {
  cursor: pointer;
}

.poi-card {
  position: relative;
  padding: 22px;
  min-height: 280px;
  height: auto;
  overflow: hidden;
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  border-radius: 20px;
}

.card-gradient-bg {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: linear-gradient(135deg, rgba(35, 165, 230, 0.05) 0%, rgba(40, 80, 150, 0.03) 100%);
  opacity: 0.7;
  transition: opacity 0.3s ease;
}

.poi-card:hover .card-gradient-bg {
  opacity: 1;
}

.poi-header {
  margin-bottom: 20px;
  z-index: 1;
}

.poi-name {
  font-size: 20px;
  font-weight: 700;
  color: #001437;
  margin: 0 0 12px 0;
  line-height: 1.3;
  letter-spacing: -0.2px;
}

.poi-category {
  display: flex;
  align-items: center;
  gap: 6px;
  color: #285096;
  font-size: 13px;
  font-weight: 500;
  background: rgba(40, 80, 150, 0.1);
  padding: 6px 14px;
  border-radius: 20px;
  width: fit-content;
  transition: background 0.3s ease;
}

.poi-card:hover .poi-category {
  background: rgba(40, 80, 150, 0.15);
}

.poi-details {
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: 12px;
  margin-bottom: 18px;
  min-height: 80px;
  z-index: 1;
}

.detail-item {
  display: flex;
  align-items: center;
  gap: 12px;
  font-size: 14px;
  font-weight: 500;
  color: #4E5965;
  transition: color 0.3s ease;
}

.poi-card:hover .detail-item {
  color: #285096;
}

.detail-icon {
  color: #23A5E6;
  min-width: 24px;
  transition: transform 0.3s ease;
}

.poi-card:hover .detail-icon {
  transform: scale(1.1);
}

.poi-actions {
  margin-top: auto;
  padding-top: 14px;
  z-index: 1;
}

.book-btn {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 10px;
  width: 100%;
  padding: 14px 20px;
  background: linear-gradient(135deg, #23A5E6 0%, #285096 100%);
  color: white;
  border: none;
  border-radius: 14px;
  font-weight: 600;
  font-size: 14px;
  cursor: pointer;
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
}

.book-btn:hover {
  transform: translateY(-2px);
  box-shadow: 0 8px 24px rgba(40, 80, 150, 0.25);
  background: linear-gradient(135deg, #23A5E6 20%, #285096 100%);
}

.book-btn:disabled {
  background: #D4D9DE;
  cursor: not-allowed;
  transform: none;
  box-shadow: none;
}

.empty-state {
  display: flex;
  justify-content: center;
  align-items: center;
  min-height: 400px;
}

.empty-card {
  text-align: center;
  padding: 52px 40px;
  max-width: 400px;
}

.empty-icon {
  color: #A2BBE6;
  margin-bottom: 24px;
}

.empty-title {
  font-size: 24px;
  font-weight: 600;
  color: #285096;
  margin: 0 0 12px 0;
}

.empty-subtitle {
  color: #4E5965;
  margin: 0 0 24px 0;
  line-height: 1.5;
}

.glass-btn {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  padding: 12px 24px;
  background: rgba(255, 255, 255, 0.9);
  backdrop-filter: blur(10px);
  border: 1px solid rgba(40, 80, 150, 0.3);
  border-radius: 12px;
  color: #285096;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  margin: 0 auto;
}

.glass-btn:hover {
  background: rgba(40, 80, 150, 0.08);
  transform: translateY(-1px);
}

.filter-item {
  margin-bottom: 18px;
}

.filter-item:last-child {
  margin-bottom: 0;
}

.compact-label {
  display: block;
  margin-bottom: 8px;
  font-weight: 500;
  color: #001437;
  font-size: 13px;
}

.booking-filter-dialog-card,
.date-picker-dialog-card {
  overflow: hidden;
  max-height: min(88vh, calc(var(--app-viewport-height, 100vh) - 32px));
}

.booking-filter-dialog-body {
  background: linear-gradient(180deg, #ffffff 0%, #f8fafc 100%);
}

.booking-dialog-actions {
  background: #f8fafc;
}

.booking-dialog-select :deep(.v-field) {
  background: #ffffff;
  border-radius: 14px;
}

.booking-dialog-select :deep(.v-field--variant-outlined) {
  --v-field-border-opacity: 1;
}

.booking-dialog-select :deep(.v-field__outline) {
  color: #d7e3f4;
}

.booking-dialog-select :deep(.v-field--focused .v-field__outline) {
  color: #23A5E6;
}

.date-picker-wrapper {
  padding: 20px;
  display: flex;
  justify-content: center;
  background: #ffffff;
  overflow: auto;
  max-height: min(68vh, calc(var(--app-viewport-height, 100vh) - 240px));
}

.date-picker-wrapper :deep(.vc-container) {
  border: none;
  box-shadow: none;
  font-family: inherit;
  width: 100%;
  max-width: 100%;
}

.date-picker-wrapper :deep(.vc-pane-layout) {
  width: 100%;
}

.date-picker-wrapper :deep(.vc-day.is-selected) {
  background-color: #23A5E6 !important;
  color: #ffffff !important;
}

.date-picker-wrapper :deep(.vc-highlight) {
  background-color: #23A5E6 !important;
  opacity: 1 !important;
}

.date-picker-wrapper :deep(.vc-highlight-bg-solid) {
  background-color: #23A5E6 !important;
}

.date-picker-wrapper :deep(.vc-day-content.is-selected) {
  background-color: #23A5E6 !important;
  color: #ffffff !important;
  font-weight: 600;
}

.date-picker-wrapper :deep(.vc-day.in-range) {
  background-color: rgba(35, 165, 230, 0.2) !important;
}

.date-picker-wrapper :deep(.vc-day.in-range .vc-day-content) {
  color: #285096 !important;
  font-weight: 500;
}

.date-picker-wrapper :deep(.vc-day.on-left),
.date-picker-wrapper :deep(.vc-day.on-right) {
  background-color: #23A5E6 !important;
}

.date-picker-wrapper :deep(.vc-day.on-left .vc-day-content),
.date-picker-wrapper :deep(.vc-day.on-right .vc-day-content) {
  background-color: #23A5E6 !important;
  color: #ffffff !important;
  font-weight: 700;
}

.date-picker-wrapper :deep(.vc-day:not(.is-disabled):hover .vc-day-content) {
  background-color: rgba(35, 165, 230, 0.3) !important;
}

.date-picker-wrapper :deep(.vc-day.is-disabled) {
  opacity: 0.4;
  cursor: not-allowed;
}

.date-picker-wrapper :deep(.vc-day.is-disabled .vc-day-content) {
  color: #CBD5E0 !important;
  background-color: #F7FAFC !important;
}

.date-picker-wrapper :deep(.vc-time-picker) {
  background-color: #F8FAFC;
  border-radius: 8px;
  padding: 12px;
  margin-top: 12px;
}

.date-picker-wrapper :deep(.vc-time-select) {
  background: #ffffff;
  border: 1px solid #E2E8F0;
  border-radius: 6px;
  color: #285096;
  font-weight: 500;
}

.date-picker-wrapper :deep(.vc-time-select:focus) {
  border-color: #23A5E6;
  outline: none;
  box-shadow: 0 0 0 3px rgba(35, 165, 230, 0.1);
}

.date-picker-dialog-card :deep(.v-alert) {
  margin-bottom: 0;
}

.poi-item-enter-active,
.poi-item-leave-active {
  transition: all 0.5s cubic-bezier(0.25, 0.8, 0.25, 1);
}

.poi-item-enter-from {
  opacity: 0;
  transform: scale(0.8) translateY(30px);
}

.poi-item-leave-to {
  opacity: 0;
  transform: scale(0.8) translateY(-30px);
}

@media (max-width: 1200px) {
  .booking-content {
    padding: 18px;
  }
  
  .grid-container {
    grid-template-columns: repeat(auto-fill, minmax(300px, 1fr));
  }
}

@media (max-width: 968px) {
  .booking-content {
    padding: 16px;
  }
  
  .compact-results-bar {
    flex-direction: column;
    align-items: stretch;
    gap: 10px;
  }
  
  .grid-container {
    grid-template-columns: repeat(auto-fill, minmax(280px, 1fr));
    gap: 18px;
  }
}

@media (max-width: 768px) {
  .booking-content {
    padding: 12px;
  }
  
  .compact-date-card {
    padding: 14px 18px;
  }
  
  .date-value {
    font-size: 16px;
  }
  
  .time-value {
    font-size: 13px;
    padding: 3px 8px;
  }
  
  .date-arrow {
    margin: 0 12px;
  }
  
  .poi-card {
    min-height: 260px;
    padding: 18px;
  }
  
  .grid-container {
    grid-template-columns: 1fr;
  }
  
  .booking-filter-dialog-card,
  .date-picker-dialog-card {
    max-height: min(84vh, calc(var(--app-viewport-height, 100vh) - 24px));
  }

  .date-picker-wrapper {
    padding: 16px;
    max-height: min(64vh, calc(var(--app-viewport-height, 100vh) - 220px));
  }
}

@media (max-width: 450px) {
  .booking-content {
    padding: 10px;
  }
  
  .compact-date-card {
    padding: 12px 16px;
  }
  
  .date-value {
    font-size: 15px;
  }
  
  .time-value {
    font-size: 12px;
    padding: 2px 6px;
  }
  
  .date-arrow {
    margin: 0 10px;
  }
  
  .poi-name {
    font-size: 18px;
  }
  
  .poi-card {
    min-height: 240px;
    padding: 16px;
  }
  
  .booking-filter-dialog-card,
  .date-picker-dialog-card {
    max-height: min(82vh, calc(var(--app-viewport-height, 100vh) - 20px));
  }

  .date-picker-wrapper {
    padding: 12px;
    max-height: min(60vh, calc(var(--app-viewport-height, 100vh) - 200px));
  }

  .date-picker-wrapper :deep(.vc-container) {
    font-size: 13px;
  }

  .booking-dialog-actions {
    padding: 12px !important;
  }
}

@media (max-width: 355px) {
  .booking-content {
    padding: 8px;
  }
  
  .compact-date-card {
    padding: 10px 14px;
  }
  
  .date-item {
    flex: 1;
    min-width: 0;
  }
  
  .date-value {
    font-size: 14px;
  }
  
  .time-value {
    font-size: 11px;
    padding: 2px 5px;
  }
  
  .date-arrow {
    margin: 0 8px;
  }
  
  .poi-card {
    min-height: 220px;
    padding: 14px;
  }
  
  .poi-name {
    font-size: 16px;
  }
  
  .book-btn {
    padding: 12px 16px;
    font-size: 13px;
  }
  
  .date-picker-wrapper {
    padding: 10px;
    max-height: min(58vh, calc(var(--app-viewport-height, 100vh) - 188px));
  }

  .date-picker-wrapper :deep(.vc-container) {
    font-size: 12px;
  }

  .booking-filter-dialog-card :deep(.v-card-title),
  .date-picker-dialog-card :deep(.v-card-title) {
    padding: 12px !important;
  }

  .booking-dialog-actions :deep(.v-btn) {
    min-width: 0;
    padding-inline: 14px !important;
  }
}
</style>
